import type {AnalyticsPlugin} from 'analytics';

import type {AnalyticsContext, NormalizedRequestContext, ServiceConfig} from '../shared/types';
import {SDK_NAME, SDK_VERSION} from '../version';

export default function contextExtractor(
  config: {service: ServiceConfig},
  getAnalyticsContext: () => AnalyticsContext | undefined,
): AnalyticsPlugin {
  return {
    name: 'contextExtractor',
    'page:rudderstackEvents': ({payload}: {payload: Record<string, unknown>}) =>
      enrichPayload(config, payload, getAnalyticsContext()),
  } as AnalyticsPlugin;
}

function buildCloudflareContext(
  requestContext: NormalizedRequestContext | undefined,
): Record<string, string | null> {
  return {
    botScore: requestContext?.headers?.cfBotScore ?? null,
    rayId: requestContext?.headers?.cfRay ?? null,
    verifiedBot: requestContext?.headers?.cfVerifiedBot ?? null,
  };
}

function extractFromRequest(analyticsContext: AnalyticsContext): NormalizedRequestContext {
  if (!analyticsContext) return {};

  const headers = analyticsContext.request?.headers;

  if (!headers) return {};

  const header = (name: string): string | null => {
    const v = headers?.[name];
    return (Array.isArray(v) ? v[0] : v) ?? null;
  };

  return {
    headers: {
      cfBotScore: header('cf-bot-score'),
      cfRay: header('cf-ray'),
      cfVerifiedBot: header('cf-verified-bot'),
    },
  };
}

function enrichPayload(
  config: {service: ServiceConfig},
  payload: Record<string, unknown>,
  analyticsContext: AnalyticsContext | undefined,
): Record<string, unknown> {
  const enrichedContext: Record<string, unknown> = {
    ...(payload.context ?? {}),
    eventClient: {name: SDK_NAME, version: SDK_VERSION},
    service: {
      name: config.service.name,
      version: config.service.version ?? undefined,
    },
  };

  if (analyticsContext) {
    const extractedContextFromRequest = extractFromRequest(analyticsContext);
    enrichedContext.cloudflare = buildCloudflareContext(extractedContextFromRequest);
  }

  return {
    ...payload,
    context: enrichedContext,
  };
}
